import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import auth from "../../services/authService";
import { getWorkbenchHelpDocs, loadWorkbenchHelpDocs } from "../../app/store/layoutUI";
import config from "../../config.json";

const Help = (props) => {
  const { helpShowHide, handleHelpShowHide, module, helpDocs: propHelpDocs } = props;
  const dispatch = useDispatch();
  const reduxHelpDocs = useSelector(getWorkbenchHelpDocs);

  const [viewerSrc, setViewerSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const retryCountRef = useRef(0);

  const effectiveHelpDocs = propHelpDocs || reduxHelpDocs;

  const helpDocUrl = useMemo(() => {
    if (!effectiveHelpDocs) return "";
    if (typeof effectiveHelpDocs === "string") return effectiveHelpDocs;
    return effectiveHelpDocs.uploadUrl || "";
  }, [effectiveHelpDocs]);

  // Request fresh help doc when modal opens
  useEffect(() => {
    if (helpShowHide) {
      retryCountRef.current = 0;
      dispatch(loadWorkbenchHelpDocs(config.HelpDocIds.adminDoc, true));
    }
  }, [helpShowHide, dispatch]);

  useEffect(() => {
    if (!helpShowHide) {
      setViewerSrc("");
      setLoading(false);
      setLoadError(null);
      return undefined;
    }

    if (!helpDocUrl) {
      setLoading(true);
      return undefined;
    }

    let objectUrl = "";
    let isCancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const response = await fetch(helpDocUrl);
        if (!response.ok) {
          // If token expired (403) and haven't retried yet, request a fresh SAS URL
          if (response.status === 403 && retryCountRef.current < 2) {
            retryCountRef.current += 1;
            console.warn("Help document SAS URL expired (403); requesting fresh URL...");
            dispatch(loadWorkbenchHelpDocs(config.HelpDocIds.adminDoc, true));
            return;
          }
          throw new Error(`Unable to load help document (HTTP ${response.status}).`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        if (!isCancelled) {
          setViewerSrc(`${objectUrl}#toolbar=0&navpanes=0&scrollbar=0`);
          setLoading(false);
          setLoadError(null);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to load help document blob:", error);
          setLoading(false);
          setLoadError(error.message || "Failed to load document");
        }
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [helpDocUrl, helpShowHide, dispatch]);

  const handleManualRetry = () => {
    setLoading(true);
    setLoadError(null);
    retryCountRef.current = 0;
    dispatch(loadWorkbenchHelpDocs(config.HelpDocIds.adminDoc, true));
  };

  const user = auth.getCurrentUser();
  const headerIconClass = module === "Support" ? "fa-comments" : "fa-info-circle";

  return (
    <React.Fragment>
      <Modal
        show={helpShowHide}
        onHide={handleHelpShowHide}
        dialogClassName="rt-profile-modal theme-primary"
        centered
        backdrop="static"
      >
        <style>{`

        /* Ensure the modal and backdrop are on top of sidebars/headers */
        .modal-backdrop {
          z-index: 1080 !important;
        }
        .modal {
          z-index: 1090 !important;
        }

        .rt-profile-modal {
          width: min(1100px, calc(100vw - 64px)) !important;
          max-width: min(1100px, calc(100vw - 64px)) !important;
          height: auto !important;
        }
        .rt-profile-modal .modal-content {
          border-radius: 0 !important;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1) !important;
          border: 1px solid #cbd5e1 !important;
          overflow: hidden !important;
          background: #ffffff !important;
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: calc(100vh - 60px) !important;
          display: flex !important;
          flex-direction: column !important;
          font-family: inherit !important;
        }
        .rt-profile-modal .modal-header {
          position: relative !important;
          padding: 16px 20px !important;
          background: #ffffff !important;
          border-bottom: 1px solid #e2e8f0 !important;
          border-radius: 0 !important;
        }
        .rt-profile-modal .modal-header::before {
          display: none !important;
        }
        .rt-profile-modal .rt-header-shell {
          width: 100% !important;
        }
        .rt-profile-modal .rt-header-top {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          gap: 12px !important;
        }
        .rt-profile-modal .rt-header-main {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
          min-width: 0 !important;
          flex: 1 !important;
        }
        .rt-profile-modal .rt-header-copy {
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          min-width: 0 !important;
        }
        .rt-profile-modal .rt-header-icon {
          width: 52px !important;
          height: 52px !important;
          min-height: 52px !important;
          border-radius: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #171717 !important;
          background: #f5f5f5 !important;
          border: 1px solid #d4d4d4 !important;
          box-shadow: 0 2px 6px -2px rgba(15,23,42,0.08) !important;
          font-size: 18px !important;
          flex-shrink: 0 !important;
        }
        .rt-profile-modal .rt-modal-title {
          display: inline-flex !important;
          align-items: center !important;
          margin: 0 !important;
          color: #1e293b !important;
          font-size: 18px !important;
          font-weight: 500 !important;
          line-height: 1.2 !important;
          min-width: 0 !important;
        }
        .rt-profile-modal .rt-modal-title-text {
          display: inline-flex !important;
          align-items: center !important;
          gap: 12px !important;
          min-width: 0 !important;
          flex-wrap: wrap !important;
        }
        .rt-profile-modal .rt-modal-title-badge {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 3px 8px !important;
          height: 22px !important;
          box-sizing: border-box !important;
          border-radius: 0 !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.02em !important;
          background: #171717 !important;
          color: #ffffff !important;
          border: 1px solid #171717 !important;
          line-height: 1 !important;
        }
        .rt-profile-modal .rt-modal-meta-row {
          display: flex !important;
          align-items: center !important;
          flex-wrap: wrap !important;
          gap: 8px !important;
          margin-top: 0 !important;
          padding-left: 0 !important;
          font-size: 12px !important;
          font-weight: 400 !important;
          color: #64748b !important;
        }
        .rt-profile-modal .rt-meta-chip {
          display: inline-flex !important;
          align-items: center !important;
          gap: 4px !important;
          background: #ffffff !important;
          padding: 3px 8px !important;
          height: 24px !important;
          min-height: 24px !important;
          box-sizing: border-box !important;
          border-radius: 0 !important;
          font-size: 11.5px !important;
          font-weight: 500 !important;
          color: #0f172a !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: none !important;
          line-height: 1 !important;
        }
        .rt-profile-modal .rt-meta-chip i,
        .rt-profile-modal .rt-meta-chip .fa {
          font-size: 11.5px !important;
          margin-right: 4px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .rt-profile-modal .rt-meta-chip i.fa-user { color: #9333ea !important; }
        .rt-profile-modal .rt-meta-chip i.fa-envelope, .rt-profile-modal .rt-meta-chip i.fa-envelope-o { color: #0284c7 !important; }
        .rt-profile-modal .rt-meta-chip i.fa-phone { color: #16a34a !important; }
        .rt-profile-modal .rt-meta-chip i.fa-hashtag { color: #d97706 !important; }
        .rt-profile-modal .rt-meta-chip i.fa-folder-open { color: #2563eb !important; }
        .rt-profile-modal .rt-meta-chip i.fa-id-badge { color: #6366f1 !important; }

        .rt-profile-modal .rt-action-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 32px !important;
          height: 32px !important;
          padding: 0 !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          color: #64748b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          cursor: pointer !important;
          transition: all 0.15s ease !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
          outline: none !important;
          font-family: inherit !important;
        }
        .rt-profile-modal .rt-action-btn:hover {
          color: #dc2626 !important;
          border-color: #fca5a5 !important;
          background: #fef2f2 !important;
          transform: translateY(-1px) !important;
        }

        .rt-profile-modal .modal-body {
          padding: 20px 24px !important;
          background: #ffffff !important;
          max-height: calc(100vh - 200px) !important;
          overflow-y: auto !important;
        }
        .rt-profile-modal .modal-footer {
          background: #ffffff !important;
          border-top: 1px solid #e2e8f0 !important;
          padding: 14px 20px !important;
          display: flex !important;
          justify-content: flex-end !important;
          gap: 10px !important;
        }
        .rt-profile-modal .wf-modal-footer-close-btn {
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 7px 18px !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          color: #1e293b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
          outline: none !important;
          cursor: pointer !important;
          transition: all 0.15s ease !important;
          font-family: inherit !important;
        }
        .rt-profile-modal .wf-modal-footer-close-btn:hover {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-color: #94a3b8 !important;
          transform: translateY(-1px) !important;
        }
        .rt-profile-modal .form-control,
        .rt-profile-modal select.form-control,
        .rt-profile-modal .input-group .form-control {
          border-radius: 0 !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: none !important;
        }
        .rt-profile-modal .form-control:focus,
        .rt-profile-modal .input-group .form-control:focus {
          border-color: #171717 !important;
          box-shadow: 0 0 0 1px #171717 !important;
        }

        .rt-support-welcome-card {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 0 !important;
          padding: 24px !important;
          margin-bottom: 16px !important;
        }
        .rt-support-welcome-card h3 {
          font-size: 15px !important;
          font-weight: 600 !important;
          color: #0f172a !important;
          margin-bottom: 12px !important;
        }
        .rt-support-welcome-card p {
          font-size: 13px !important;
          color: #475569 !important;
          line-height: 1.6 !important;
          margin-bottom: 10px !important;
        }
        .rt-support-welcome-card .support-signature {
          margin-top: 16px !important;
          padding-top: 12px !important;
          border-top: 1px solid #e2e8f0 !important;
        }
        .rt-support-welcome-card .support-signature h3 {
          font-size: 13px !important;
          font-weight: 600 !important;
          color: #171717 !important;
          margin: 0 0 2px 0 !important;
        }
  `}</style>
        <Modal.Header>
          <div className="rt-header-shell">
            <div className="rt-header-top">
              <div className="rt-header-main">
                <i className={`rt-header-icon fa ${headerIconClass}`} aria-hidden="true" />
                <div className="rt-header-copy">
                  <h2 className="rt-modal-title">
                    <span className="rt-modal-title-text">
                      <span>{module === "Support" ? "Add Issue" : "Help Document"}</span>
                      <span className="rt-modal-title-badge">
                        {module === "Support" ? "Support" : "Help"}
                      </span>
                    </span>
                  </h2>
                  <div className="rt-modal-meta-row">
                    <span className="rt-meta-chip">
                      <i className="fa fa-folder-open" aria-hidden="true" style={{ color: "#2563eb", marginRight: "4px" }} />
                      <span>Module: {module || "General"}</span>
                    </span>
                    {user?.name && (
                      <span className="rt-meta-chip">
                        <i className="fa fa-user" aria-hidden="true" style={{ color: "#9333ea", marginRight: "4px" }} />
                        <span>{user.name}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="rt-action-btn"
                onClick={handleHelpShowHide}
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {module === "Support" && (
            <div className="rt-support-welcome-card">
              <h3>Welcome to our Customer Support Portal!</h3>
              <p>
                In this portal we offer you an easy way to register any issue you may encounter while working and using
                various functions and features.
              </p>
              <p>
                Once you register an issue and submit, it will be received by our dedicated and committed customer and
                technical support staff who will review and provide instant fix(s), if required.
              </p>
              <p>Our customer and technical support staff will also update and notify you of the action taken.</p>
              <div className="support-signature">
                <h3>Thank you.</h3>
                <h3>WorkFINITY®</h3>
                <h3>Customer and Technical Support</h3>
              </div>
            </div>
          )}

          {loading && (
            <div style={{ height: "450px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px" }}>
              <i className="fa fa-circle-o-notch fa-spin fa-2x" style={{ color: "#2563eb" }} />
              <span style={{ fontSize: "14px", color: "#64748b", fontWeight: 500 }}>Loading help document...</span>
            </div>
          )}

          {loadError && !loading && (
            <div style={{ height: "350px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px", padding: "20px", textAlign: "center" }}>
              <i className="fa fa-exclamation-triangle fa-2x" style={{ color: "#ef4444" }} />
              <div style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b" }}>Unable to load help document</div>
              <div style={{ fontSize: "13px", color: "#64748b", maxWidth: "450px" }}>The document access link has expired or is temporarily unavailable. Click below to refresh.</div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleManualRetry}
                style={{ padding: "8px 18px", borderRadius: "0", background: "#2563eb", borderColor: "#2563eb", color: "#ffffff" }}
              >
                <i className="fa fa-refresh mr-1" /> Reload Help Document
              </button>
            </div>
          )}

          {viewerSrc && !loading && (
            <div style={{ height: "650px", width: "100%", maxWidth: "1020px", margin: "auto" }}>
              <iframe
                title="Help PDF Viewer"
                src={viewerSrc}
                width="100%"
                height="100%"
                style={{ border: "none", borderRadius: "0", boxShadow: "0 4px 12px rgba(15,23,42,0.08)" }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="wf-modal-footer-close-btn"
            onClick={handleHelpShowHide}
          >
            <i className="fa fa-times" aria-hidden="true" />
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Help;
